<template>
  <div class="language-toggle" v-if="showToggle">
    <span class="change-language-label">Change language:</span>
    <b-dropdown
      variant="link"
      no-caret
      :right="dropdownRight"
      class="language-dropdown"
    >
      <template #button-content>
        <span class="language-btn">
          <span class="language-flag">{{ currentFlag }}</span>
          <span class="language-name">{{ currentLanguageName }}</span>
          <i class="fa fa-chevron-down language-caret"></i>
        </span>
      </template>

      <b-dropdown-item
        v-for="lang in availableLanguages"
        :key="lang.code"
        @click="selectLanguage(lang.code)"
        :active="currentLanguage === lang.code"
      >
        <span class="dropdown-language-item">
          <span class="language-flag">{{ lang.flag }}</span>
          <span class="language-label">{{ lang.name }}</span>
          <i v-if="currentLanguage === lang.code" class="fa fa-check language-check"></i>
        </span>
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
import {
  getLanguagePreference,
  setLanguagePreference,
  formSupportsLanguage,
  getLanguageDisplayName,
  getLanguageFlag,
  getAvailableLanguagesForForm
} from '../utils/languageHelper';

export default {
  name: 'LanguageToggle',

  props: {
    // Form configuration to check which languages are supported
    formConfig: {
      type: Object,
      default: null
    },
    // Force show toggle even if form doesn't have non-English languages
    forceShow: {
      type: Boolean,
      default: false
    },
    // Initial language (optional - will use preference if not provided)
    initialLanguage: {
      type: String,
      default: null
    },
    // Dropdown alignment
    dropdownRight: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      currentLanguage: 'en'
    };
  },

  computed: {
    showToggle() {
      // Show if forceShow is true OR if form supports any non-English language
      if (this.forceShow) return true;
      if (this.formConfig) {
        return formSupportsLanguage(this.formConfig);
      }
      return false;
    },

    availableLanguages() {
      // Build language list dynamically from form's supportedLanguages
      if (this.formConfig) {
        return getAvailableLanguagesForForm(this.formConfig);
      }
      // Fallback: just English
      return [{ code: 'en', name: 'English', flag: getLanguageFlag('en') }];
    },

    currentLanguageName() {
      return getLanguageDisplayName(this.currentLanguage);
    },

    currentFlag() {
      return getLanguageFlag(this.currentLanguage);
    }
  },

  created() {
    // Initialize language
    if (this.initialLanguage) {
      this.currentLanguage = this.initialLanguage;
    } else {
      this.currentLanguage = getLanguagePreference();
    }

    // Emit initial language
    this.$emit('language-changed', this.currentLanguage);
  },

  watch: {
    initialLanguage(newLang) {
      if (newLang && newLang !== this.currentLanguage) {
        this.currentLanguage = newLang;
      }
    }
  },

  methods: {
    selectLanguage(langCode) {
      if (langCode !== this.currentLanguage) {
        this.currentLanguage = langCode;
        setLanguagePreference(langCode);
        this.$emit('language-changed', langCode);
      }
    }
  }
};
</script>

<style scoped>
.language-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.change-language-label {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
}

.language-dropdown {
  margin: 0;
}

.language-dropdown :deep(.btn) {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #333;
  font-size: 14px;
  transition: all 0.2s ease;
}

.language-dropdown :deep(.btn:hover) {
  background: #f8f9fa;
  border-color: #ccc;
}

.language-dropdown :deep(.btn:focus) {
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.language-btn {
  display: flex;
  align-items: center;
  gap: 6px;
}

.language-flag {
  font-size: 16px;
  line-height: 1;
}

.language-name {
  font-weight: 500;
}

.language-caret {
  font-size: 10px;
  margin-left: 2px;
  opacity: 0.7;
}

.dropdown-language-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.language-label {
  flex: 1;
}

.language-check {
  color: #28a745;
  font-size: 12px;
}

/* Dropdown menu styling */
.language-dropdown :deep(.dropdown-menu) {
  min-width: 140px;
  padding: 4px 0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.language-dropdown :deep(.dropdown-item) {
  padding: 8px 12px;
  font-size: 14px;
}

.language-dropdown :deep(.dropdown-item:hover) {
  background-color: #f0f7ff;
}

.language-dropdown :deep(.dropdown-item.active) {
  background-color: #e8f4ff;
  color: #333;
}

/* Mobile responsive */
@media (max-width: 576px) {
  .language-name {
    display: none;
  }

  .language-dropdown :deep(.btn) {
    padding: 6px 10px;
  }

  .language-caret {
    display: none;
  }
}
</style>
